import { useContext, useState, useRef } from "react";
import { Box, IconButton, Typography, TextField } from "@mui/material";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import CalendarTodayIcon from "@mui/icons-material/CalendarToday";
import { DatePicker, LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import dayjs from "dayjs";
import { PlannerContext } from "../../context/planner/PlannerContext";

/**
 * Checks if a date string represents today
 * @param {string} dateString - Date in YYYY-MM-DD format
 * @returns {boolean}
 */
const isToday = (dateString) => {
  const date = new Date(dateString + "T00:00:00");
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  date.setHours(0, 0, 0, 0);
  return date.getTime() === today.getTime();
};

/**
 * Formats a date for display based on its relation to today
 * @param {string} dateString - Date in YYYY-MM-DD format
 * @returns {string} Formatted date string
 */
const formatDateDisplay = (dateString) => {
  const date = new Date(dateString + "T00:00:00"); // Ensure local timezone
  const today = new Date();
  today.setHours(0, 0, 0, 0);

  const tomorrow = new Date(today);
  tomorrow.setDate(tomorrow.getDate() + 1);

  // Reset times for comparison
  const dateOnly = new Date(date);
  dateOnly.setHours(0, 0, 0, 0);

  if (dateOnly.getTime() === today.getTime()) {
    return "Today";
  }

  if (dateOnly.getTime() === tomorrow.getTime()) {
    return "Tomorrow";
  }

  // For other dates, show day name and formatted date
  const dayNames = [
    "Sunday",
    "Monday",
    "Tuesday",
    "Wednesday",
    "Thursday",
    "Friday",
    "Saturday",
  ];
  const dayName = dayNames[date.getDay()];

  const day = date.getDate();
  const month = date.toLocaleDateString("en-US", { month: "short" });

  // Add ordinal suffix to day
  const ordinalSuffix = (n) => {
    const s = ["th", "st", "nd", "rd"];
    const v = n % 100;
    return n + (s[(v - 20) % 10] || s[v] || s[0]);
  };

  return `${dayName}, ${ordinalSuffix(day)} ${month}`;
};

const DateNavigator = () => {
  const { currentDate, goToPreviousDay, goToNextDay, setCurrentDate } =
    useContext(PlannerContext);
  const [isDatePickerOpen, setIsDatePickerOpen] = useState(false);
  const dateTextRef = useRef(null);

  const displayText = formatDateDisplay(currentDate);
  const canGoBack = !isToday(currentDate);

  // Get today's date in YYYY-MM-DD format for minDate
  const today = new Date();
  const todayString = `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, "0")}-${String(today.getDate()).padStart(2, "0")}`;

  const handleDateChange = (newDate) => {
    if (newDate && newDate.isValid()) {
      const formattedDate = newDate.format("YYYY-MM-DD");
      setCurrentDate(formattedDate);
    }
    setIsDatePickerOpen(false);
  };

  const handleDateTextClick = () => {
    setIsDatePickerOpen(true);
  };

  return (
    <Box className="date-navigator">
      <IconButton
        onClick={goToPreviousDay}
        className="date-navigator_arrow"
        aria-label="Previous day"
        size="small"
        disabled={!canGoBack}
      >
        <ChevronLeftIcon />
      </IconButton>

      <Box
        className="date-navigator_date-container"
        onClick={handleDateTextClick}
        sx={{
          display: "flex",
          alignItems: "center",
          cursor: "pointer",
        }}
        ref={dateTextRef}
      >
        <CalendarTodayIcon
          sx={{ color: "primary.dark", fontSize: 24, mr: 1 }}
        />
        <Typography variant="h2" className="date-navigator_date">
          {displayText}
        </Typography>
      </Box>

      <LocalizationProvider dateAdapter={AdapterDayjs}>
        <DatePicker
          open={isDatePickerOpen}
          onClose={() => setIsDatePickerOpen(false)}
          value={dayjs(currentDate)}
          onChange={handleDateChange}
          minDate={dayjs(todayString)}
          renderInput={(params) => (
            <TextField
              {...params}
              sx={{
                position: "absolute",
                opacity: 0,
                pointerEvents: "none",
                width: 0,
                height: 0,
              }}
            />
          )}
          PopperProps={{
            anchorEl: dateTextRef.current,
            placement: "bottom",
          }}
        />
      </LocalizationProvider>

      <IconButton
        onClick={goToNextDay}
        className="date-navigator_arrow"
        aria-label="Next day"
        size="small"
      >
        <ChevronRightIcon />
      </IconButton>
    </Box>
  );
};

export default DateNavigator;
